import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Divider from '@mui/material/Divider';
import AppAppBar from '../AppAppBar';
import Footer from '../Footer';

const PALETTE = [
  {
    title: 'Markt & Problem',
    items: [
      { label: 'Echtes Problem (Painkiller)', value: 'Problem: Painkiller', color: 'primary' },
      { label: 'Nett zu haben (Vitamin)', value: 'Problem: Vitamin', color: 'primary' },
      { label: 'B2B', value: 'Zielgruppe: B2B', color: 'primary' },
      { label: 'B2C', value: 'Zielgruppe: B2C', color: 'primary' },
      { label: 'Nische', value: 'Markt: Nische', color: 'primary' },
      { label: 'Masse', value: 'Markt: Masse', color: 'primary' },
    ],
  },
  {
    title: 'Lösung & USP',
    items: [
      { label: '10x Günstiger', value: 'USP: 10x Günstiger', color: 'success' },
      { label: '10x Schneller', value: 'USP: 10x Schneller', color: 'success' },
      { label: 'Software', value: 'Produkt: Software', color: 'success' },
      { label: 'Radikal einfach', value: 'UX: Extreme Einfachheit', color: 'success' },
    ],
  },
  {
    title: 'Geschäftsmodell',
    items: [
      { label: 'Abo (SaaS)', value: 'Monetarisierung: Abonnement (SaaS)', color: 'warning' },
      { label: 'Freemium', value: 'Monetarisierung: Freemium', color: 'warning' },
      { label: 'Premium', value: 'Preis: Premium', color: 'warning' },
    ],
  },
  {
    title: 'Wachstum',
    items: [
      { label: 'Netzwerkeffekte', value: 'Wachstum: Netzwerkeffekte', color: 'secondary' },
      { label: 'Viral', value: 'Wachstum: Virales Marketing', color: 'secondary' },
    ],
  },
  {
    title: 'Technologie',
    items: [
      { label: 'KI / AI', value: 'Technologie: Künstliche Intelligenz', color: 'info' },
      { label: 'Big Data', value: 'Technologie: Big Data-Analyse', color: 'info' },
    ],
  },
];

const STEPS = ['Deine Idee', 'Ressourcen & Team', 'Strategie-Baukasten', 'Ergebnis'];

export default function GenerateIdea({ mode, toggleColorMode }) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [prompt, setPrompt] = React.useState('');
  const [skills, setSkills] = React.useState('');
  const [team, setTeam] = React.useState('');
  const [education, setEducation] = React.useState('');
  const [selected, setSelected] = React.useState([]);
  const [result, setResult] = React.useState('');
  const [sources, setSources] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [liveMessage, setLiveMessage] = React.useState('');

  const resultRef = React.useRef(null);

  // Validation: each step must have min. content to proceed
  const isStep0Valid = prompt.trim().length > 5;
  const isStep1Valid = skills.trim().length > 0 || team.trim().length > 0 || education.trim().length > 0;
  const isStep2Valid = selected.length > 0;

  const handleNext = () => {
    if (activeStep === 0 && !isStep0Valid) {
      setLiveMessage('Bitte beschreibe deine Grundidee oder dein Problem.');
      return;
    }
    if (activeStep === 1 && !isStep1Valid) {
      setLiveMessage('Bitte fülle mindestens ein Feld aus.');
      return;
    }
    if (activeStep === 2 && !isStep2Valid) {
      setLiveMessage('Bitte wähle mindestens einen Baustein aus.');
      return;
    }
    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Selection handlers (click-to-toggle)
  const toggleSelect = (value) => {
    setSelected((s) => (s.includes(value) ? s.filter((x) => x !== value) : [...s, value]));
  };

  const removeSelected = (value) => {
    setSelected((s) => s.filter((x) => x !== value));
  };

  const reset = () => {
    setActiveStep(0);
    setPrompt('');
    setSkills('');
    setTeam('');
    setEducation('');
    setSelected([]);
    setResult('');
    setSources([]);
    setLiveMessage('');
  };
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY || '';
  const apiUrl = apiKey
    ? `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`
    : null;

  async function fetchWithBackoff(url, options, retries = 3, delay = 1000) {
    try {
      const res = await fetch(url, options);
      if (!res.ok && res.status === 429 && retries > 0) {
        await new Promise((r) => setTimeout(r, delay));
        return fetchWithBackoff(url, options, retries - 1, delay * 2);
      }
      return res;
    } catch (err) {
      if (retries > 0) {
        await new Promise((r) => setTimeout(r, delay));
        return fetchWithBackoff(url, options, retries - 1, delay * 2);
      }
      throw err;
    }
  }

  const detectContradiction = (text) => {
    if (!text) return false;
    const t = text.toLowerCase();
    return t.includes('aber') || t.includes('ohne') || t.includes('without');
  };

  const generate = async () => {
    if (!isStep0Valid || !isStep1Valid || !isStep2Valid) {
      setLiveMessage('Bitte fülle alle Schritte aus.');
      return;
    }

    setLoading(true);
    setResult('');
    setSources([]);
    setLiveMessage('Generierung gestartet');

    const components = selected.join(', ');
    const resourcesContext = `
Fähigkeiten: ${skills || 'keine angegeben'}
Team: ${team || 'keine angegeben'}
Ausbildung: ${education || 'keine angegeben'}
    `.trim();

    const systemPrompt = `Du bist ein Innovations- und Startup-Analyst, spezialisiert auf strategische Frameworks und TRIZ. Antworte auf Deutsch.
Beziehe die Ressourcen und das Team des Nutzers in deine Analyse ein.`;

    const payload = {
      contents: [
        {
          parts: [
            {
              text: `Main Input: "${prompt}"\nRessourcen:\n${resourcesContext}\nStrategic Pillars: ${components}`,
            },
          ],
        },
      ],
      systemInstruction: { parts: [{ text: systemPrompt }] },
      tools: apiKey ? [{ google_search: {} }] : undefined,
    };

    try {
      if (apiUrl) {
        const response = await fetchWithBackoff(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`API error ${response.status}`);
        }
        const json = await response.json();
        const candidate = json.candidates?.[0];
        const text = candidate?.content?.parts?.[0]?.text || 'Keine Antwort erhalten.';
        setResult(text);
        const grounding = candidate?.groundingMetadata?.groundingAttributions || [];
        const srcs = grounding.map((g) => g.web).filter(Boolean);
        setSources(srcs);
      } else {
        // Fallback
        const isContradiction = detectContradiction(prompt);
        let simulated = '';
        if (isContradiction) {
          simulated += `TRIZ-Lösung: Wir haben ein technisches Widerspruch erkannt.\n`;
        }
        simulated += `Deine Grundidee: ${prompt}\n`;
        simulated += `Ressourcen: Fähigkeiten (${skills}), Team (${team}), Ausbildung (${education})\n`;
        simulated += `Strategische Bausteine: ${components}\n`;
        simulated += `Name: ${prompt.split(' ')[0] || 'Unicorn'} Studio\n`;
        simulated += `Pitch: Ein fokussiertes Produkt, das Ihren Bedarf adressiert.`;
        await new Promise((r) => setTimeout(r, 700));
        setResult(simulated);
        setSources([]);
      }
      setLiveMessage('Generierung abgeschlossen');
      setActiveStep(STEPS.length - 1);
      setTimeout(() => resultRef.current?.focus(), 50);
    } catch (err) {
      console.error(err);
      setResult(`Ein Fehler ist aufgetreten: ${err.message}`);
      setLiveMessage('Fehler bei der Generierung');
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <AppAppBar mode={mode} toggleColorMode={toggleColorMode} />
      <Container
        component="main"
        sx={{ mt: { xs: 12, md: 14 }, mb: { xs: 12, md: 14 }, px: { xs: 2, sm: 3, md: 4 } }}
        maxWidth="lg"
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Idee generieren
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Beschreibe deine Vorstellung in einfachen Schritten. Wir helfen dir, kreative Ideen zu entwickeln.
        </Typography>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* ARIA live region */}
        <Box
          component="div"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(1px, 1px, 1px, 1px)',
            whiteSpace: 'nowrap',
            border: 0,
            padding: 0,
            margin: -1,
          }}
          aria-live="polite"
        >
          {liveMessage}
        </Box>

        {/* Step 0: Grundidee / Problem */}
        {activeStep === 0 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Deine Grundidee oder dein Problem
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Beschreibe ausführlich, was du lösen möchtest oder welche Idee du verwirklichen willst.
            </Typography>
            <TextField
              id="idea-input"
              label="Grundidee / Problem"
              placeholder="Beispiel: Ich möchte einen digitalen Marktplatz für nachhaltige Produkte schaffen..."
              multiline
              rows={6}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
              <Button
                aria-label="Abbrechen"
                variant="outlined"
                onClick={reset}
                disabled={loading}
                fullWidth
                sx={{ order: { xs: 1, md: 1 } }}
              >
                Abbrechen
              </Button>
              <Button
                aria-label="Weiter zum nächsten Schritt"
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={loading || !isStep0Valid}
                fullWidth
                sx={{ fontWeight: 700, px: 3, order: { xs: 0, md: 2 } }}
              >
                Weiter
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 1: Ressourcen & Team */}
        {activeStep === 1 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Ressourcen & Team
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Teile uns deine Fähigkeiten, dein Team und deine Ausbildung mit. Das hilft uns, eine passende Lösung zu entwickeln.
            </Typography>

            <TextField
              id="skills-input"
              label="Deine Fähigkeiten"
              placeholder="Beispiel: Webentwicklung, UX-Design, Marketing..."
              multiline
              rows={3}
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            />

            <TextField
              id="team-input"
              label="Dein Team"
              placeholder="Beispiel: Co-Founder mit Sales-Erfahrung, 2 Entwickler, 1 Designer..."
              multiline
              rows={3}
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            />

            <TextField
              id="education-input"
              label="Ausbildung & Erfahrung"
              placeholder="Beispiel: Informatik-Studium, 5 Jahre Startup-Erfahrung, Entrepreneurship-Bootcamp..."
              multiline
              rows={3}
              value={education}
              onChange={(e) => setEducation(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
              <Button
                aria-label="Zurück zum vorherigen Schritt"
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                fullWidth
                sx={{ order: { xs: 1, md: 1 } }}
              >
                Zurück
              </Button>
              <Button
                aria-label="Weiter zum nächsten Schritt"
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={loading || !isStep1Valid}
                fullWidth
                sx={{ fontWeight: 700, px: 3, order: { xs: 0, md: 2 } }}
              >
                Weiter
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 2: Strategie-Baukasten */}
        {activeStep === 2 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Strategie-Baukasten
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Wähle die Bausteine, die am besten zu deiner Idee passen. Du kannst mehrere auswählen.
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  {PALETTE.map((cat) => (
                    <Box key={cat.title} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        {cat.title}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {cat.items.map((it) => {
                          const isSelected = selected.includes(it.value);
                          return (
                            <Chip
                              key={it.value}
                              label={it.label}
                              color={it.color}
                              clickable
                              variant={isSelected ? 'filled' : 'outlined'}
                              onClick={() => toggleSelect(it.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleSelect(it.value);
                                }
                              }}
                              aria-pressed={isSelected}
                              sx={{ minHeight: 40 }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  sx={{
                    p: 2,
                    height: { md: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    position: { md: 'sticky' },
                    top: { md: 110 },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Ausgewählte Bausteine
                  </Typography>
                  {selected.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selected.map((s) => {
                        const parts = s.split(': ');
                        const display = parts.length > 1 ? parts[1] : s;
                        return (
                          <Chip
                            key={s}
                            label={display}
                            onClick={() => removeSelected(s)}
                            color="default"
                            sx={{ bgcolor: 'action.selected', minHeight: 40 }}
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ minHeight: 56, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Noch keine Bausteine ausgewählt. Tippe auf Bausteine im Baukasten, um sie hinzuzufügen.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
              <Button
                aria-label="Zurück zum vorherigen Schritt"
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                fullWidth
                sx={{ order: { xs: 1, md: 1 } }}
              >
                Zurück
              </Button>
              <Button
                aria-label="Idee generieren"
                variant="contained"
                color="primary"
                onClick={generate}
                disabled={loading || !isStep2Valid}
                fullWidth
                sx={{ fontWeight: 700, px: 3, order: { xs: 0, md: 2 } }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Idee generieren'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 3: Result */}
        {activeStep === 3 && (
          <Paper sx={{ p: 4 }}>
            {result ? (
              <>
                <Paper
                  ref={resultRef}
                  tabIndex={-1}
                  role="region"
                  aria-label="KI-generiertes Konzept"
                  variant="outlined"
                  sx={{ p: 3, mb: 3 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      background: 'linear-gradient(90deg,#9f7aea,#6366f1)',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }}
                  >
                    KI-generiertes Konzept
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{result}</Typography>
                  {sources && sources.length > 0 && (
                    <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Basierend auf (Quellen):
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {sources.map((s, i) => (
                          <li key={i}>
                            <a href={s.uri} target="_blank" rel="noopener noreferrer">
                              {s.title || s.uri}
                            </a>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
                <Divider sx={{ my: 3 }} />
              </>
            ) : null}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
              <Button
                aria-label="Von vorne beginnen"
                variant="outlined"
                onClick={reset}
                fullWidth
                sx={{ order: { xs: 1, md: 1 } }}
              >
                Neue Idee
              </Button>
              <Button
                aria-label="Weitere Ideen generieren"
                variant="contained"
                color="primary"
                onClick={() => setActiveStep(2)}
                fullWidth
                sx={{ fontWeight: 700, px: 3, order: { xs: 0, md: 2 } }}
              >
                Andere Bausteine wählen
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
      <Footer />
    </React.Fragment>
  );
}